import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const slides = [
  {
    image: "/images/hero/office1.jpg",
    headline: "Client Thank-Yous. Team Milestones. Staff Appreciation.",
    subtitle: "Make Your Team Feel Special",
    buttonLabel: "Shop Assorted Boxes",
    buttonUrl: "shop/gifts/assorted-dessert-boxes?event=team",
  },
  {
    image: "/images/hero/graduation.png",
    headline: "Cheers to New Beginnings.",
    subtitle: "Graduations Made Sweeter",
    buttonLabel: "Shop Graduation Cakes",
    buttonUrl: "/shop/cakes/specialty-cakes?event=graduation",
  },
  {
    image: "/images/hero/anniversary.jpg",
    headline: "Celebrate the Years, Celebrate the Love.",
    subtitle: "Mark Every Milestone Deliciously",
    buttonLabel: "Shop Anniversary Cakes",
    buttonUrl: "/shop/cakes/specialty-cakes?event=anniversary",
  },
];

const HeroCarousel = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const { image, headline, subtitle, buttonLabel, buttonUrl } =
    slides[currentSlide];

  const handleButtonClick = () => {
    if (buttonUrl) {
      navigate(buttonUrl);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        margin: "0 auto",
        position: "relative",
        height: "100vh",
        maxHeight: { xs: "600px", md: "none" },
      }}>
      {/* Background Image */}
      <Box
        component="img"
        src={image}
        alt="Hero background"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          maxHeight: { xs: "600px", md: "none" },
          objectFit: "cover",
          zIndex: 1,
        }}
      />

      {/* Dark Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          maxHeight: { xs: "600px", md: "none" },
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          zIndex: 2,
        }}
      />

      {/* Content Layer */}
      <Box
        sx={{
          position: "relative",
          zIndex: 3,
          width: "100%",
          height: "100%",
          maxHeight: { xs: "600px", md: "none" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: 600, md: 800 },
            boxSizing: "border-box",
            textAlign: "center",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            px: { xs: 2, sm: 4, md: 6 },
          }}>
          <motion.div
            key={headline}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.8rem", sm: "2.5rem", md: "4rem" },
                fontWeight: 800,
                color: "white",
                mb: 3,
                lineHeight: 1.2,
              }}>
              {headline}
            </Typography>

            {subtitle && buttonUrl && (
              <motion.div
                key={subtitle}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}>
                <Button
                  variant="contained"
                  size={isMobile ? "small" : "large"}
                  onClick={handleButtonClick}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: { xs: "1rem", md: "1.2rem" },
                    px: 5,
                    py: 1.5,
                    borderRadius: 3,
                    backgroundColor: "primary.main",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  }}>
                  {buttonLabel}
                </Button>
              </motion.div>
            )}
          </motion.div>

          {/* Dots indicator */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 4,
              gap: 1.5,
            }}>
            {slides.map((_, index) => (
              <ButtonBase
                key={index}
                onClick={() => setCurrentSlide(index)}
                sx={{
                  width: currentSlide === index ? 14 : 10,
                  height: currentSlide === index ? 14 : 10,
                  borderRadius: "50%",
                  backgroundColor:
                    currentSlide === index ? "primary.main" : "grey.400",
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HeroCarousel;
